/**
 * Custom hook for managing unit selection state.
 * Connects UnitSelectionController with unit selection store.
 */

import { useEffect } from 'react';
import { useUnitSelectionStore } from '@shared/stores/unitSelectionStore';
import { Unit } from '@game/units/Unit';

interface UseUnitSelectionOptions {
  scene: unknown | null;
}

/**
 * Hook for managing unit selection state integration.
 */
export function useUnitSelection({ scene }: UseUnitSelectionOptions) {
  const setSelectedUnits = useUnitSelectionStore((state) => state.setSelectedUnits);

  useEffect(() => {
    if (!scene || typeof scene !== 'object' || scene === null) {
      return;
    }

    const sceneWithMethods = scene as {
      getUnitSelectionController?: () => {
        setOnSelectionChanged?: (callback: (selectedUnits: Unit[]) => void) => void;
      } | null;
    };

    const selectionController = sceneWithMethods.getUnitSelectionController?.();
    
    if (selectionController?.setOnSelectionChanged) {
      // Set up callback to update store when selection changes
      selectionController.setOnSelectionChanged((selectedUnits: Unit[]) => {
        const unitIds = selectedUnits.map((unit) => unit.getId());
        setSelectedUnits(unitIds);
      });
    }

    // Cleanup: remove callback when component unmounts or scene changes
    return () => {
      if (selectionController?.setOnSelectionChanged) {
        selectionController.setOnSelectionChanged(() => {
          // Empty handler for cleanup
        });
      }
    };
  }, [scene, setSelectedUnits]);
}

